import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { SwUpdate } from '@angular/service-worker';
import { ApplicationRef, computed, DOCUMENT, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  concat,
  EMPTY,
  from,
  fromEvent,
  interval,
  merge,
  Observable,
  pipe,
  of,
  race,
  timer,
} from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { withLogger } from '@ngrx-traits/signals';
import { APP_ROUTE_SEGMENTS } from '@core/navigation';
import { ToastService } from '@core/toast';
import { AppUpdatesAria } from '../localization/app-updates.aria';
import { AppUpdatesLabels } from '../localization/app-updates.labels';
import {
  ACTIVATE_UPDATE_TIMEOUT_MS,
  APP_UPDATE_RELOAD_PATH,
  BACKGROUND_RELOAD_THRESHOLD_MS,
  MIN_RETURN_CHECK_INTERVAL_MS,
  PENDING_REQUESTS_TIMEOUT_MS,
  RETURN_UPDATE_CHECK_TIMEOUT_MS,
  UNRECOVERABLE_RELOAD_DELAY_MS,
  UPDATE_CHECK_TIMEOUT_MS,
} from '../models/app-updates.model';
import { PAGE_RELOAD } from '../utils/page-reload';

type AppUpdatesState = {
  isCheckForUpdateLoading: boolean;
  isActivateUpdateLoading: boolean;
  isDownloading: boolean;
  isUpdateAvailable: boolean;
  currentVersion: string | null;
  latestVersion: string | null;
  lastChecked: Date | null;
  error: string | null;
};

const initialState: AppUpdatesState = {
  isCheckForUpdateLoading: false,
  isActivateUpdateLoading: false,
  isDownloading: false,
  isUpdateAvailable: false,
  currentVersion: null,
  latestVersion: null,
  lastChecked: null,
  error: null,
};

const APP_URL = `/${APP_ROUTE_SEGMENTS.ROOT}`;

function isAppUrl(url: string): boolean {
  return (
    url === APP_URL ||
    url.startsWith(`${APP_URL}/`) ||
    url.startsWith(`${APP_URL}?`) ||
    url.startsWith(`${APP_URL}#`)
  );
}

function routePath(url: string): string {
  return url.split(/[?#]/)[0];
}

function hasCanDeactivateGuard(route: ActivatedRouteSnapshot): boolean {
  return (
    (route.routeConfig?.canDeactivate?.length ?? 0) > 0 ||
    route.children.some(hasCanDeactivateGuard)
  );
}

export const AppUpdatesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _swUpdate: inject(SwUpdate),
    _liveAnnouncer: inject(LiveAnnouncer),
    _router: inject(Router),
    _document: inject(DOCUMENT),
    _toastService: inject(ToastService),
    _reloadPage: inject(PAGE_RELOAD),
    _appRef: inject(ApplicationRef),
  })),

  withComputed(({ _swUpdate, error, currentVersion, latestVersion }) => {
    return {
      isEnabled: computed(() => _swUpdate.isEnabled),

      hasError: computed(() => error() !== null),

      errorMessage: computed(() => {
        if (!error()) return null;
        return 'Update-Prüfung fehlgeschlagen. Bitte versuchen Sie es später erneut.';
      }),

      versionDisplay: computed(() => {
        const current = currentVersion();
        const latest = latestVersion();

        if (!current || !latest) {
          return null;
        }

        return {
          current: current.substring(0, 8),
          latest: latest.substring(0, 8),
        };
      }),
    };
  }),
  withMethods(
    ({
      _swUpdate,
      _liveAnnouncer,
      _router,
      _document,
      _toastService,
      _reloadPage,
      _appRef,
      ...state
    }) => {
      const checkSilently$ = (
        timeoutMs = UPDATE_CHECK_TIMEOUT_MS
      ): Observable<void> => {
        const checkForUpdate$ = from(_swUpdate.checkForUpdate()).pipe(
          tap(() =>
            patchState(state, { lastChecked: new Date(), error: null })
          ),
          map(() => void 0)
        );

        const timeout$ = timer(timeoutMs).pipe(
          switchMap(() => throwError(() => new Error('Update check timed out')))
        );

        return race(checkForUpdate$, timeout$).pipe(
          catchError((error: unknown) => {
            const errorMsg =
              error instanceof Error
                ? error.message
                : `Failed to check for updates: ${error}`;
            patchState(state, { error: errorMsg });
            return of(void 0);
          })
        );
      };

      const activateAndReload$ = (): Observable<void> => {
        patchState(state, { isActivateUpdateLoading: true });
        return race(
          from(_appRef.whenStable()),
          timer(PENDING_REQUESTS_TIMEOUT_MS)
        ).pipe(
          take(1),
          switchMap(() =>
            race(
              from(_swUpdate.activateUpdate()),
              timer(ACTIVATE_UPDATE_TIMEOUT_MS).pipe(map(() => false))
            )
          ),
          catchError(() => of(false)),
          tap(() => _reloadPage()),
          map(() => void 0)
        );
      };

      const canReloadWithoutUserData = (): boolean =>
        state.isUpdateAvailable() &&
        !state.isActivateUpdateLoading() &&
        _document.visibilityState === 'visible' &&
        isAppUrl(_router.url) &&
        !hasCanDeactivateGuard(_router.routerState.snapshot.root) &&
        _document.querySelector('.ng-dirty') === null;

      const hasNavigationState = (): boolean => {
        const navigation =
          _router.currentNavigation() ?? _router.lastSuccessfulNavigation();
        return Object.keys(navigation?.extras.state ?? {}).length > 0;
      };

      return {
        checkForUpdate: rxMethod<void>(
          pipe(
            switchMap(() => {
              patchState(state, { isCheckForUpdateLoading: true });

              if (!_swUpdate.isEnabled) {
                patchState(state, {
                  isCheckForUpdateLoading: false,
                  error: 'Service Worker ist nicht aktiviert',
                });
                return EMPTY;
              }

              const checkForUpdate$ = from(_swUpdate.checkForUpdate());

              const timeout$ = timer(UPDATE_CHECK_TIMEOUT_MS).pipe(
                switchMap(() =>
                  throwError(
                    () => new Error('Update check timed out after 10 seconds')
                  )
                )
              );

              return race(checkForUpdate$, timeout$).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      isCheckForUpdateLoading: false,
                      lastChecked: new Date(),
                      error: null,
                    });
                  },
                  error: (error: unknown) => {
                    const errorMsg =
                      error instanceof Error
                        ? error.message
                        : `Failed to check for updates: ${error}`;
                    patchState(state, {
                      isCheckForUpdateLoading: false,
                      error: errorMsg,
                    });
                  },
                })
              );
            }),
            catchError((error) => {
              patchState(state, {
                isCheckForUpdateLoading: false,
                error: `Unexpected error: ${error}`,
              });
              return EMPTY;
            })
          )
        ),
        activateUpdate: rxMethod<void>(
          pipe(
            switchMap(() => {
              patchState(state, { isActivateUpdateLoading: true });

              return from(_swUpdate.activateUpdate()).pipe(
                tapResponse({
                  next: (activated) => {
                    patchState(state, { isActivateUpdateLoading: false });
                    if (activated) {
                      _reloadPage();
                    }
                  },
                  error: (error) => {
                    const errorMsg = `Failed to activate update: ${error}`;
                    patchState(state, {
                      isActivateUpdateLoading: false,
                      error: errorMsg,
                    });
                  },
                })
              );
            })
          )
        ),
        reloadWithUpdate: rxMethod<void>(
          pipe(
            filter(() => !state.isActivateUpdateLoading()),
            exhaustMap(() => {
              patchState(state, { isActivateUpdateLoading: true });

              return from(
                _router.navigateByUrl(`/${APP_UPDATE_RELOAD_PATH}`, {
                  skipLocationChange: true,
                })
              ).pipe(
                catchError(() => of(false)),
                switchMap((navigated) => {
                  if (!navigated) {
                    patchState(state, { isActivateUpdateLoading: false });
                    return EMPTY;
                  }
                  return activateAndReload$();
                })
              );
            })
          )
        ),
        startPeriodicChecks: rxMethod<void>(
          pipe(
            switchMap(() => {
              if (!_swUpdate.isEnabled) {
                return EMPTY;
              }

              // Use timer-based initial delay instead of zone stability (zoneless compatible)
              const initialDelay$ = timer(3000);
              const everyFifteenMinutes$ = interval(15 * 60 * 1000);

              return concat(initialDelay$, everyFifteenMinutes$).pipe(
                switchMap(() => checkSilently$())
              );
            })
          )
        ),
        _checkOnReturn: rxMethod<void>(
          pipe(
            switchMap(() => {
              if (!_swUpdate.isEnabled) {
                return EMPTY;
              }

              let hiddenSince: number | null = null;
              const window = _document.defaultView;
              const online$ = window
                ? fromEvent(window, 'online').pipe(
                    exhaustMap(() => checkSilently$())
                  )
                : EMPTY;
              const visible$ = fromEvent(_document, 'visibilitychange').pipe(
                filter(() => {
                  if (_document.visibilityState === 'hidden') {
                    hiddenSince = Date.now();
                    return false;
                  }
                  return true;
                }),
                map(() => {
                  const returnedAfterLongAbsence =
                    hiddenSince !== null &&
                    Date.now() - hiddenSince >= BACKGROUND_RELOAD_THRESHOLD_MS;
                  hiddenSince = null;
                  return returnedAfterLongAbsence;
                }),
                filter((returnedAfterLongAbsence) => {
                  const lastChecked = state.lastChecked();
                  return (
                    returnedAfterLongAbsence ||
                    lastChecked === null ||
                    Date.now() - lastChecked.getTime() >=
                      MIN_RETURN_CHECK_INTERVAL_MS
                  );
                }),
                switchMap((returnedAfterLongAbsence) =>
                  returnedAfterLongAbsence
                    ? checkSilently$(RETURN_UPDATE_CHECK_TIMEOUT_MS).pipe(
                        filter(() => canReloadWithoutUserData()),
                        exhaustMap(() => activateAndReload$())
                      )
                    : checkSilently$()
                )
              );

              return merge(visible$, online$);
            })
          )
        ),
        _reloadOnNavigation: rxMethod<void>(
          pipe(
            switchMap(() => {
              let previousPath = routePath(_router.url);
              return _router.events.pipe(
                filter(
                  (event): event is NavigationEnd =>
                    event instanceof NavigationEnd
                ),
                filter((event) => {
                  const path = routePath(event.urlAfterRedirects);
                  const pathChanged = path !== previousPath;
                  previousPath = path;
                  return (
                    pathChanged &&
                    state.isUpdateAvailable() &&
                    !state.isActivateUpdateLoading() &&
                    isAppUrl(event.urlAfterRedirects) &&
                    !hasNavigationState()
                  );
                })
              );
            }),
            exhaustMap(() => activateAndReload$())
          )
        ),
        _initializeVersionListener: () => {
          if (!_swUpdate.isEnabled) {
            patchState(state, {
              isUpdateAvailable: false,
              lastChecked: new Date(),
            });
            return;
          }

          patchState(state, {
            isUpdateAvailable: false,
            lastChecked: new Date(),
          });

          _swUpdate.versionUpdates.subscribe((evt) => {
            switch (evt.type) {
              case 'VERSION_DETECTED':
                patchState(state, { isDownloading: true, error: null });
                break;

              case 'VERSION_READY':
                patchState(state, {
                  isDownloading: false,
                  isUpdateAvailable: true,
                  currentVersion: evt.currentVersion.hash,
                  latestVersion: evt.latestVersion.hash,
                  lastChecked: new Date(),
                });
                void _liveAnnouncer.announce(
                  AppUpdatesAria.updateReadyAnnouncement()
                );
                break;

              case 'VERSION_INSTALLATION_FAILED': {
                const errorMsg = `Failed to install app version '${evt.version.hash}': ${evt.error}`;
                patchState(state, { isDownloading: false, error: errorMsg });
                break;
              }
            }
          });

          _swUpdate.unrecoverable
            .pipe(
              take(1),
              tap(() =>
                _toastService.show({
                  type: 'warning',
                  title: AppUpdatesLabels.unrecoverableTitle(),
                  message: AppUpdatesLabels.unrecoverableMessage(),
                  duration: UNRECOVERABLE_RELOAD_DELAY_MS,
                })
              ),
              switchMap(() => timer(UNRECOVERABLE_RELOAD_DELAY_MS))
            )
            .subscribe(() => _reloadPage());
        },
      };
    }
  ),
  withHooks(
    ({
      startPeriodicChecks,
      _initializeVersionListener,
      _checkOnReturn,
      _reloadOnNavigation,
    }) => ({
      onInit() {
        _initializeVersionListener();
        startPeriodicChecks();
        _checkOnReturn();
        _reloadOnNavigation();
      },
    })
  ),
  withLogger({
    name: 'AppUpdatesStore',
    showDiff: true,
    filter: (store) => ({
      isDownloading: store.isDownloading,
      isUpdateAvailable: store.isUpdateAvailable,
      currentVersion: store.currentVersion,
      latestVersion: store.latestVersion,
      lastChecked: store.lastChecked,
      versionDisplay: store.versionDisplay,
      isEnabled: store.isEnabled,
      hasError: store.hasError,
      error: store.error,
      isActivateUpdateLoading: store.isActivateUpdateLoading,
      isCheckForUpdateLoading: store.isCheckForUpdateLoading,
    }),
  })
);
